import { ThumbsDownMinor, ThumbsUpMinor } from '@shopify/polaris-icons'
import React, { useCallback, useContext, useState } from 'react'
import { Button } from 'src/components/shared/Button'
import { SensitiveActionWithPictureModal } from 'src/components/shared/SubmitActionWithPictureModal'
import { colors } from 'src/theme'
import tw from 'twin.macro'
import { ApproveSubcontractorRequestsModal } from '../approve-subcontractor-requests/ApproveSubcontractorRequestsModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { ContractorDetails } from 'src/interfaces/contractor'
import { handleApiError } from 'src/utils/errorHandlers'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DeclineSubcontractorRequests } from 'src/constants/actionTypes'
import { useDispatch } from 'react-redux'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { ContractorDetailsModalContext } from 'src/components/contractor-details/ContractorDetailsModal.context'

interface Props {
  subcontractorDetails: ContractorDetails
  onSubmit(): void
  onClose(): void
}

export const SubcontractorRequestsActions = ({
  subcontractorDetails,
  onSubmit,
  onClose,
}: Props) => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)
  const { statusIsUpdating } = useContext(ContractorDetailsModalContext)

  const [isApproveContractorsOpen, setIsApproveContractorsOpen] = useState(false)
  const [isConfirmDeclineOpen, setIsConfirmDeclineOpen] = useState(false)

  const handleOpenConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(true)
  }, [])

  const handleCloseConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(false)
  }, [])

  const handleOpenApproveContractors = useCallback(() => {
    setIsApproveContractorsOpen(true)
  }, [])

  const handleCloseApproveContractors = useCallback(() => {
    setIsApproveContractorsOpen(false)
  }, [onClose])

  const handleSubmit = useCallback(() => {
    setIsApproveContractorsOpen(false)
    setIsConfirmDeclineOpen(false)

    onSubmit()
    onClose()
  }, [onSubmit, onClose])

  const handleDeclineContractor = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        DeclineSubcontractorRequests.request({
          selectedSubcontractorRequestIds: [subcontractorDetails.partnerDriverRelationId],
        }),
      )

      reload()

      showToast('Contractor declined. Ask the master contractor to link him again if needed.')
    } catch (err) {
      handleApiError(err)
    }

    handleSubmit()
  }, [subcontractorDetails.partnerDriverRelationId, handleSubmit, reload])

  return (
    <div css={tw`py-4 mx-4 border-0 border-t border-solid border-[#EDEDED]`}>
      <div css={tw`flex flex-col gap-2`}>
        <Button
          fullWidth
          size="small"
          color="green"
          css={[tw`flex items-center justify-center rounded`]}
          endIcon={<ThumbsUpMinor width={20} fill={colors.COLORS_GREEN_500} />}
          disabled={statusIsUpdating}
          onClick={handleOpenApproveContractors}
        >
          Approve
        </Button>
        <Button
          fullWidth
          size="small"
          color="red"
          css={[tw`flex items-center justify-center rounded`]}
          endIcon={<ThumbsDownMinor width={20} fill={colors.COLORS_RED_500} />}
          disabled={statusIsUpdating}
          onClick={handleOpenConfirmDecline}
        >
          Decline
        </Button>
      </div>
      <SensitiveActionWithPictureModal
        isOpen={isConfirmDeclineOpen}
        title="Are you sure you want to decline this contractor?"
        text="Declining a contractor will remove them from the table. If you want to approve this contractor later, ask the master contractor to link them to you again."
        submitLabel="Decline"
        onClose={handleCloseConfirmDecline}
        onSubmit={handleDeclineContractor}
        imageSrc={NotFoundImage}
      />
      {subcontractorDetails?.partnerDriverRelationId && (
        <ApproveSubcontractorRequestsModal
          isOpen={isApproveContractorsOpen}
          onClose={handleCloseApproveContractors}
          onSubmit={onClose}
          subcontractorRequestsSelection={[subcontractorDetails.partnerDriverRelationId]}
        />
      )}
    </div>
  )
}
